import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { DatabaseIcon, SparklesIcon } from "lucide-react";

import { ScorecardInputVariablesField } from "../Inputs/ScorecardInputVariablesField";
import { ScorecardSourceSelector } from "../Inputs/ScorecardSourceSelector";

import { ReadonlyProvider } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import {
  AssertionVariableToken,
  MultipleAssertions,
} from "@/components/PowerBlock/Blocks/categories/llm-evals/LLMAssertionBlock/MultipleAssertions";
import { Toggle } from "@/components/Toggle";
import { BasicTooltip } from "@/components/ui/Tooltip";

interface LlmAssertionFieldsProps {
  sourceColumnId: string;
  onSourceColumnChange: (columnId: string) => void;
  assertions: string[];
  detectedPromptVariables: string[];
  onAssertionsChange: (assertions: string[]) => void;
  isImportMode: boolean;
  onImportModeChange: (isImportMode: boolean) => void;
  promptSourceColumnId: string;
  onPromptSourceColumnChange: (columnId: string) => void;
  variableMappings: Record<string, string>;
  onVariableMappingsChange: (mappings: Record<string, string>) => void;
  sourceColumnOptions: { id: string; title: string }[];
  hasUnmappedLlmVariables: boolean;
}

export const LlmAssertionFields = ({
  sourceColumnId,
  onSourceColumnChange,
  assertions,
  detectedPromptVariables,
  onAssertionsChange,
  isImportMode,
  onImportModeChange,
  promptSourceColumnId,
  onPromptSourceColumnChange,
  variableMappings,
  onVariableMappingsChange,
  sourceColumnOptions,
  hasUnmappedLlmVariables,
}: LlmAssertionFieldsProps) => (
  <>
    <SectionGroup
      title="Assertion"
      description="Define what the AI should check for"
      icon={<SparklesIcon className="h-4 w-4" />}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-700">Import from Column</span>
          <BasicTooltip content="Enable to import assertions from a dataset column instead of writing them directly">
            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
          </BasicTooltip>
        </div>
        <Toggle enabled={isImportMode} setEnabled={onImportModeChange} />
      </div>

      {isImportMode ? (
        <div className="space-y-4">
          <div className="text-xs">Import assertions from a dataset column.</div>
          <ScorecardSourceSelector
            value={promptSourceColumnId}
            onValueChange={onPromptSourceColumnChange}
            placeholder="Select a source..."
            options={sourceColumnOptions}
            name="Assertion Source"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-xs">
            Write your assertion below. You can optionally use{" "}
            <AssertionVariableToken name="column" />
            to insert dataset values.
          </div>

          <ReadonlyProvider readonly={false}>
            <MultipleAssertions
              assertions={assertions}
              assertionVariables={detectedPromptVariables}
              onAssertionsChange={onAssertionsChange}
            />
          </ReadonlyProvider>

          {detectedPromptVariables.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">Map variables to columns</p>
              <ScorecardInputVariablesField
                variables={detectedPromptVariables}
                sourceColumnOptions={sourceColumnOptions}
                variableMappings={variableMappings}
                onVariableMappingsChange={onVariableMappingsChange}
              />
              {hasUnmappedLlmVariables ? (
                <p className="text-xs text-amber-700">
                  Map every assertion variable before saving.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </SectionGroup>

    <SectionGroup
      title="Data Source"
      description="Select the data to evaluate"
      icon={<DatabaseIcon className="h-4 w-4" />}
    >
      <ScorecardSourceSelector
        value={sourceColumnId}
        onValueChange={onSourceColumnChange}
        placeholder="Select a source..."
        options={sourceColumnOptions}
        name="Source"
      />
    </SectionGroup>
  </>
);
